// src/services/sursysProductionAPI.js
//
// Real-data source for the Production Dashboard (Laporan Stok Harian, Analisis
// Kategori, Heatmap). Backed by /api/production/* on the sursys backend.
// Branches are dynamic (per Accurate warehouse), so the UI must not hardcode them.

import { surroundingSys } from './api';
import { TENANTS } from './sursysLogisticAPI';

let activeTenant = TENANTS.SUMA;
const tenantParams = () => ({ company_id: activeTenant.company_id, system_name: activeTenant.system_name });

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

// Palette for dynamic branch columns.
const PALETTE = [
  { bg: '#fff7ed', color: '#9a3412', border: '#fed7aa' },
  { bg: '#fdf2f8', color: '#9d174d', border: '#fbcfe8' },
  { bg: '#f0fdfa', color: '#134e4a', border: '#99f6e4' },
  { bg: '#eef2ff', color: '#3730a3', border: '#c7d2fe' },
  { bg: '#fefce8', color: '#854d0e', border: '#fef08a' },
  { bg: '#f0f9ff', color: '#075985', border: '#bae6fd' },
  { bg: '#faf5ff', color: '#6b21a8', border: '#e9d5ff' },
  { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
];

// Derive priority from how deep the national deficit is.
const priorityOf = (defisit, spBelum) => {
  if (defisit < 0 && Math.abs(defisit) >= spBelum * 0.5) return 'URGENT';
  if (defisit < 0) return 'HIGH';
  return 'NORMAL';
};

// Fetch the daily-stock dataset (Tab 1 + Heatmap). Returns dynamic branches + items
// shaped like the legacy dummy data so the existing UI can render it.
export const fetchProductionDashboard = async (limit = 30) => {
  const { data } = await surroundingSys.get('/production/dashboard', {
    params: { ...tenantParams(), limit },
  });
  const branchNames = data?.branches || [];
  const branches = branchNames.map((name, i) => ({
    key: name,
    label: name,
    ...PALETTE[i % PALETTE.length],
  }));

  const items = (data?.items || []).map((it) => {
    const cabang = {};
    Object.entries(it.cabang || {}).forEach(([b, m]) => {
      cabang[b] = {
        stok_akhir: num(m.stok_akhir),
        keluar_harian: num(m.keluar_harian),
        keluar_2026: num(m.keluar_tahunan),
        sp_belum: num(m.sp_belum),
        stok_min_sp: num(m.stok_min_sp),
        trend: 0,
      };
    });
    const nas = it.nasional || {};
    const defisit = num(nas.stok_min_sp);
    return {
      kode: it.kode,
      nama: it.nama,
      kategori: it.kategori,
      satuan: it.satuan,
      stok_pusat: cabang['PUSAT']?.stok_akhir ?? num(nas.stok_akhir),
      // Production-status fields are not in Accurate; SPK board (gapfamily) owns them.
      status: { step: 0, rencana: false, proses: false, realisasi: false, label: 'Belum' },
      prioritas: priorityOf(defisit, num(nas.sp_belum)),
      pic: '-',
      aging_sp: 0,
      progress: 0,
      rencana_qty: 0,
      realisasi_qty: 0,
      eta: '-',
      cabang,
      nasional: {
        keluar_harian: num(nas.keluar_harian),
        keluar_tahunan: num(nas.keluar_tahunan),
        stok_akhir: num(nas.stok_akhir),
        sp_belum: num(nas.sp_belum),
        stok_min_sp: defisit,
        trend: 0,
      },
    };
  });

  return { branches, items };
};

// Fetch the category YoY analytics (Tab 2). Shape matches categoryAnalyticsData.
export const fetchCategoryAnalytics = async (topCategories = 6) => {
  const { data } = await surroundingSys.get('/production/category-analytics', {
    params: { ...tenantParams(), limit: topCategories },
  });
  return data?.data || [];
};
